import React, { useState, useMemo } from "react";
import Icon from "../../../components/AppIcon";
import DateRangeFilter from "../../dashboard-overview/components/DateRangeFilter";
import RecentDistributionsTable from "../../dashboard-overview/components/RecentDistributionsTable";
import DistributionDetailModal from "../../dashboard-overview/components/DistributionDetailModal";
import { useTroncRecords } from "../../../hooks/useTroncRecords";
import { useLocationContext } from "../../../contexts/LocationContext";
import { distributionApi } from "../../../services/api/distribution";

/**
 * Distribution History tab — existing report migrated into a tab component.
 */
const DistributionHistoryTab = ({ shouldFetchData }) => {
  const { currentLocation } = useLocationContext();
  const [selectedDateRange, setSelectedDateRange] = useState("30days");
  const [customFrom, setCustomFrom] = useState(null);
  const [customTo, setCustomTo] = useState(null);
  const [selectedDistribution, setSelectedDistribution] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  const { records: troncRecords, loading } = useTroncRecords({
    autoFetch: shouldFetchData,
  });

  const displayRecords = currentLocation
    ? (troncRecords || []).filter((r) => r.locationId === currentLocation.id)
    : troncRecords || [];

  const distributedRecords = useMemo(() => {
    const distributed = displayRecords.filter(
      (r) => r.isDistributed || r.status === "Distributed" || r.status === 3,
    );
    const now = new Date();
    let cutoffDate = new Date();
    switch (selectedDateRange) {
      case "7days":
        cutoffDate.setDate(now.getDate() - 7);
        break;
      case "30days":
        cutoffDate.setDate(now.getDate() - 30);
        break;
      case "thisMonth":
        cutoffDate = new Date(now.getFullYear(), now.getMonth(), 1);
        break;
      case "lastMonth":
        cutoffDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        break;
      case "custom":
        cutoffDate = customFrom ? new Date(customFrom) : new Date(0);
        break;
      default:
        cutoffDate.setDate(now.getDate() - 30);
    }
    return distributed
      .filter((r) => {
        const recordDate = new Date(r.periodStart);
        if (selectedDateRange === "lastMonth") {
          const lastMonthStart = new Date(
            now.getFullYear(),
            now.getMonth() - 1,
            1,
          );
          const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0);
          return recordDate >= lastMonthStart && recordDate <= lastMonthEnd;
        }
        if (selectedDateRange === "custom") {
          if (!customFrom || !customTo) return true;
          return (
            recordDate >= new Date(customFrom) &&
            recordDate <= new Date(new Date(customTo).setHours(23, 59, 59, 999))
          );
        }
        return recordDate >= cutoffDate;
      })
      .sort((a, b) => new Date(b.periodStart) - new Date(a.periodStart))
      .map((r) => ({
        id: r.id,
        date: new Date(r.periodStart).toLocaleDateString("en-GB", {
          weekday: "short",
          day: "numeric",
          month: "short",
          year: "numeric",
        }),
        amount: `£${(r.amount || 0).toFixed(2)}`,
        rawAmount: r.amount || 0,
        employeeCount: r.employeeCount || "—",
        method: r.distributionRuleName || r.distributionMethod || "—",
        status: "Completed",
        isLocked: true,
        hasManualOverride: r.hasManualOverride || false,
        locationName: r.locationName || "",
      }));
  }, [displayRecords, selectedDateRange]);

  const summary = useMemo(() => {
    const total = distributedRecords.reduce((sum, r) => sum + r.rawAmount, 0);
    return { count: distributedRecords.length, total };
  }, [distributedRecords]);

  const handleViewDetails = async (distributionId) => {
    try {
      const response =
        await distributionApi.getDistributionDetail(distributionId);
      if (response.success && response.data) {
        setSelectedDistribution(response.data);
        setShowDetailModal(true);
      }
    } catch (error) {
      console.error("Failed to fetch distribution details:", error);
    }
  };

  const handleExport = () => {
    if (distributedRecords.length === 0) return;
    const headers = [
      "Date",
      "Amount",
      "Employees",
      "Method",
      "Status",
      "Location",
    ];
    const rows = distributedRecords.map((d) => [
      d.date,
      d.amount,
      d.employeeCount,
      d.method,
      d.status,
      d.locationName,
    ]);
    const csv = [headers, ...rows].map((row) => row.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `tronc-distributions-${selectedDateRange}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4">
      <DateRangeFilter
        onFilterChange={setSelectedDateRange}
        onExport={handleExport}
        onCustomRangeChange={(from, to) => {
          setCustomFrom(from);
          setCustomTo(to);
        }}
      />

      {!loading && distributedRecords.length > 0 && (
        <div className="flex flex-wrap items-center gap-4 px-1 text-sm text-muted-foreground">
          <span>
            <strong className="text-foreground">{summary.count}</strong>{" "}
            {summary.count === 1 ? "distribution" : "distributions"}
          </span>
          <span className="hidden sm:inline">•</span>
          <span>
            Total distributed:{" "}
            <strong className="text-foreground">
              £{summary.total.toFixed(2)}
            </strong>
          </span>
        </div>
      )}

      {loading ? (
        <div className="card p-8 text-center">
          <div className="spinner mx-auto mb-4" />
          <p className="text-muted-foreground">Loading distributions...</p>
        </div>
      ) : distributedRecords.length === 0 ? (
        <div className="card p-8 text-center">
          <div className="empty-state-icon mx-auto mb-4">
            <Icon name="FileText" size={32} />
          </div>
          <h3 className="text-lg font-semibold mb-2">No distributed records</h3>
          <p className="text-muted-foreground">
            TRONC records that have been distributed will appear here.
          </p>
        </div>
      ) : (
        <RecentDistributionsTable
          distributions={distributedRecords}
          onViewDetails={handleViewDetails}
        />
      )}

      {showDetailModal && selectedDistribution && (
        <DistributionDetailModal
          distribution={selectedDistribution}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedDistribution(null);
          }}
        />
      )}
    </div>
  );
};

export default DistributionHistoryTab;
